import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ListChecks, Pencil, Trash2, Eye, Download, AlertTriangle } from 'lucide-react';
import ax from '@/lib/apiClient';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MyPlans = ({ refreshKey, onEdit }) => {
  const [plans, setPlans] = useState([]);
  const [openPlan, setOpenPlan] = useState(null);

  const fetchPlans = useCallback(async () => {
    try { const { data } = await ax.get(`${API}/trivia/plans`); setPlans(data.plans); } catch {}
  }, []);
  useEffect(() => { fetchPlans(); }, [fetchPlans, refreshKey]);

  const remove = async (p) => {
    if (!window.confirm(`Delete the plan "${p.name}"?`)) return;  // delete asks first
    try { await ax.delete(`${API}/trivia/plans/${p.id}`); toast.success('Plan deleted'); fetchPlans(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const downloadBighat = async (p) => {
    try {
      let part = 1;
      let total = 1;
      do {
        // eslint-disable-next-line no-await-in-loop
        const resp = await ax.get(`${API}/trivia/plans/${p.id}/bighat`, { params: { part }, responseType: 'blob' });
        total = parseInt(resp.headers['x-parts-total'] || '1', 10);
        const url = URL.createObjectURL(resp.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = total === 1 ? `${p.name}.bighat` : `${p.name} (part ${part} of ${total}).bighat`;
        a.click();
        URL.revokeObjectURL(url);
        part += 1;
      } while (part <= total);
      if (total > 1) toast.success(`Show split into ${total} transfer files (50 MB cap) — all downloaded`);
    } catch (err) {
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        try { toast.error(JSON.parse(text).detail); } catch { toast.error('Download failed'); }
      } else toast.error(err.response?.data?.detail || 'Download failed');
    }
  };

  return (
    <Card className="glass-panel border-0" data-testid="my-plans-card">
      <CardHeader>
        <CardTitle className="text-white text-lg flex items-center gap-2">
          <ListChecks className="w-5 h-5 text-[#fbdd68]" /> My plans
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {plans.length === 0 && <p className="text-sm text-[#8892b0] italic" data-testid="plans-empty">No show plans yet — build one above.</p>}
        {plans.map((p) => (
          <div key={p.id} data-testid={`plan-row-${p.id}`}
            className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-lg bg-[rgba(20,27,80,0.4)] border border-[rgba(251,221,104,0.06)]">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white font-medium truncate flex items-center gap-2">
                {p.name}
                {p.has_locked_rounds && (
                  <span data-testid={`plan-warning-${p.id}`} title="A round in this plan was locked after it was saved"
                    className="inline-flex items-center gap-1 text-[10px] text-[#fbdd68]"><AlertTriangle className="w-3.5 h-3.5" />round now locked</span>
                )}
              </p>
              <p className="text-xs text-[#8892b0] truncate">
                {p.venue_name} · {p.host_name} · {p.round_count} rounds · {new Date(p.created_at).toLocaleDateString()}
              </p>
              {(p.imported_by || []).length > 0 && (
                <p className="text-[10px] text-[#4ade80] truncate" data-testid={`plan-imported-${p.id}`}>
                  Imported on {p.imported_by.map((i) => i.program_name).join(', ')}
                </p>
              )}
            </div>
            <Badge className={p.status === 'ready' ? 'bg-[#22c55e]/10 text-[#22c55e] border-[#22c55e]/30' : 'bg-[#8892b0]/10 text-[#8892b0] border-[#8892b0]/30'}>{p.status}</Badge>
            <div className="flex gap-1 shrink-0">
              <Button size="sm" variant="ghost" data-testid={`plan-open-${p.id}`} onClick={() => setOpenPlan(openPlan?.id === p.id ? null : p)}
                className="text-[#fbdd68] hover:bg-[#fbdd68]/10 h-8"><Eye className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" data-testid={`plan-edit-${p.id}`} onClick={() => onEdit(p)}
                className="text-white hover:bg-white/10 h-8"><Pencil className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" data-testid={`plan-download-${p.id}`} onClick={() => downloadBighat(p)}
                title="Download the .bighat show file"
                className="text-[#4ade80] hover:bg-[#4ade80]/10 h-8"><Download className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" data-testid={`plan-delete-${p.id}`} onClick={() => remove(p)}
                className="text-[#ef4444] hover:bg-[#ef4444]/10 h-8"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
        {openPlan && (
          <div data-testid="plan-detail" className="p-3 rounded-lg bg-[rgba(20,27,80,0.6)] border border-[rgba(251,221,104,0.15)]">
            <p className="text-xs font-bold text-[#fbdd68] mb-1">{openPlan.name}</p>
            {openPlan.slots.map((s) => (
              <p key={s.order} className="text-xs text-[#c8d0e8]">
                {s.order}. [{s.type}] {s.name}{s.locked_now ? ' — now locked at this venue' : ''}
              </p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
